import React, { useEffect, useRef, useState } from "react";
import { ErrorBanner, Spinner } from "../components/common";
import { consequence, describe } from "./proposals";
import { useCopilot } from "./useCopilot";

const SUGGESTIONS = [
  "Give me an overview of this run.",
  "Which compound has the weakest inhibition?",
  "What has been excluded so far?",
];

/* Icons are inline so the panel needs no icon dependency and no network. They
   are decorative — every control they sit in carries its own accessible name,
   which is why they are `aria-hidden`. */
const icon = (paths) => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {paths}
  </svg>
);

const SparkIcon = icon(<path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />);
const NewChatIcon = icon(
  <>
    <path d="M12 5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-6" />
    <path d="M17.5 3.5a2.1 2.1 0 0 1 3 3L13 14l-4 1 1-4z" />
  </>
);
const CloseIcon = icon(
  <>
    <path d="M6 6l12 12" />
    <path d="M18 6L6 18" />
  </>
);
const SendIcon = icon(
  <>
    <path d="M12 19V5" />
    <path d="M5 12l7-7 7 7" />
  </>
);

/** The "Experimental" tag, in the two places the copilot is named. */
function ExperimentalTag() {
  return (
    <span
      className="copilot-badge"
      title="Experimental: the copilot has not been validated on a real analysis. Check anything that matters against the run."
    >
      Experimental
    </span>
  );
}

/**
 * The copilot, beside the curation it is talking about.
 *
 * Two rules are visible in this component rather than merely documented. An
 * answer that quoted a number the analysis did not produce is withheld by the
 * server and arrives flagged, and is shown as a refusal. And a proposal is
 * rendered as a confirm card: it has changed nothing, and confirming it is the
 * scientist making the ordinary curation call under their own name.
 *
 * A third is new: the panel says **Experimental** wherever it names itself. The
 * copilot has never been run against a real model on a real analysis, and a
 * chat window that looks finished invites more trust than this one has earned.
 *
 * And a fourth, for the same reason: when no model is connected the panel says
 * so and the composer is disabled. It used to answer from a deterministic
 * stand-in, which is indistinguishable from a model at the reading end — an
 * empty chat window that explains itself is a smaller failure than a fluent
 * one nobody can audit.
 */
export default function CopilotPanel({ runId, apply, onCurationChanged }) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [cleared, setCleared] = useState(false);
  const log = useRef(null);
  const field = useRef(null);
  const {
    availability,
    messages,
    pending,
    busy,
    starting,
    error,
    ask,
    confirm,
    reject,
    newChat,
    dismissError,
  } = useCopilot({
    runId,
    apply,
    onCurationChanged,
  });

  /* Three states, not two. `null` is "the server has not said yet", and it
     must not be drawn as either — an enabled box that turns out to be dead
     invites a question that is never sent, and a disabled one that turns out
     to be alive reads as a broken feature. */
  const unavailable = availability.available === false;
  const settled = availability.available !== null;
  const standIn = availability.kind === "stand-in";

  useEffect(() => {
    if (log.current) log.current.scrollTop = log.current.scrollHeight;
  }, [messages, pending, open]);

  /* The composer grows with the question rather than scrolling a one-line box,
     so a scientist can see a long question before sending it. */
  useEffect(() => {
    const box = field.current;
    if (!box) return;
    box.style.height = "auto";
    box.style.height = `${Math.min(box.scrollHeight, 120)}px`;
  }, [question, open]);

  function send() {
    if (!question.trim() || busy) return;
    setCleared(false);
    ask(question);
    setQuestion("");
  }

  function submit(event) {
    event.preventDefault();
    send();
  }

  /* Enter sends, shift+Enter starts a line — what a chat window is expected to
     do. Without this the only way to send is the button, which reads as a form. */
  function onKeyDown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  }

  async function startNewChat() {
    await newChat();
    setCleared(true);
  }

  if (!open) {
    return (
      <button type="button" className="copilot-launcher" onClick={() => setOpen(true)}>
        {SparkIcon}
        Copilot
        <ExperimentalTag />
      </button>
    );
  }

  return (
    <aside className="copilot" aria-label="Analysis copilot">
      <header className="copilot-head">
        <span className="copilot-avatar">{SparkIcon}</span>
        <h3>Copilot</h3>
        <ExperimentalTag />
        <button
          type="button"
          className="icon-button"
          onClick={startNewChat}
          disabled={unavailable || busy || starting || messages.length === 0}
          title="Start a new chat. The conversation so far is kept with the run."
          aria-label="Start a new chat"
        >
          {NewChatIcon}
        </button>
        <button
          type="button"
          className="icon-button"
          onClick={() => setOpen(false)}
          title="Hide the copilot"
          aria-label="Hide the copilot"
        >
          {CloseIcon}
        </button>
      </header>

      <ErrorBanner error={error} onDismiss={dismissError} />

      <div className="copilot-log" ref={log}>
        {unavailable && (
          <div className="copilot-unavailable" role="status">
            <p className="copilot-empty-lead">The copilot is unavailable</p>
            <p>{availability.reason}</p>
          </div>
        )}
        {standIn && (
          <p className="copilot-note copilot-standin" role="status">
            You are talking to a stand-in, not a language model. It matches keywords against
            this run. Its answers are for trying the panel out — do not read them as an
            assistant's judgement.
          </p>
        )}
        {!unavailable && messages.length === 0 && (
          <div className="copilot-empty">
            {cleared && (
              <p className="copilot-note">
                New chat. The previous conversation is still kept with this run — it was not
                deleted, the copilot just no longer has it in mind.
              </p>
            )}
            <p className="copilot-empty-lead">Ask about this run.</p>
            <p className="copilot-empty-note">
              It reads this run and can move the screen. Every figure it gives is copied from the
              analysis; it cannot change anything without you confirming it.
            </p>
            <div className="copilot-suggestions">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="chip"
                  onClick={() => {
                    setCleared(false);
                    ask(s);
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((message, i) => (
          <div
            key={i}
            className={`copilot-message ${message.role}${
              message.grounded === false ? " withheld" : ""
            }`}
          >
            {message.content}
            {message.grounded === false && (
              <span className="copilot-note">Answer withheld: it was not traceable to this run.</span>
            )}
          </div>
        ))}

        {pending.map((proposal, i) => {
          const cost = consequence(proposal);
          return (
          <div key={`proposal-${i}`} className="copilot-proposal">
            <strong>Suggested change</strong>
            <p>{describe(proposal)}</p>
            {cost && (
              <div className="copilot-consequence" role="note">
                <p>{cost.warning}</p>
                {cost.reasons.length > 0 && (
                  <ul>
                    {cost.reasons.map((reason, r) => (
                      <li key={`discard-${r}`}>{reason}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <p className="muted">Nothing has changed yet. Confirming records it under your name.</p>
            <div className="button-row">
              <button className="primary" disabled={busy} onClick={() => confirm(i)}>
                Confirm
              </button>
              <button disabled={busy} onClick={() => reject(i)}>
                Reject
              </button>
            </div>
          </div>
          );
        })}

        {busy && (
          <div className="copilot-typing" role="status" aria-label="Thinking">
            <span />
            <span />
            <span />
          </div>
        )}
        {starting && <Spinner label="Starting a new chat…" />}
      </div>

      <form className={`copilot-ask${unavailable ? " disabled" : ""}`} onSubmit={submit}>
        <textarea
          ref={field}
          rows={1}
          aria-label="Ask about this run"
          placeholder={
            unavailable ? "The copilot is unavailable" : "Ask about this run…"
          }
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={!settled || unavailable}
        />
        <button
          type="submit"
          className="copilot-send"
          disabled={!settled || unavailable || busy || !question.trim()}
          aria-label="Send"
          title="Send"
        >
          {SendIcon}
        </button>
      </form>
      <p className="copilot-foot">
        {unavailable
          ? "No model is connected to this installation."
          : "Experimental — it quotes this run and changes nothing without you."}
      </p>
    </aside>
  );
}
